<template>
  <ErrorMessage v-if="lessonError">
    <p>{{ lessonError }}</p>
    <p>
      <ThreeDButton icon="redo" @click="fetchLessonData">
        {{ $t('actions.retry') }}
      </ThreeDButton>
    </p>
  </ErrorMessage>
  <LoadingOverlay v-else-if="!lesson"/>
  <div v-else class="chapter-section-lesson">
    <h1 class="chapter-section-heading">
      {{ lesson.title }}<TextToSpeech :text="lesson.title" />
    </h1>
    <Accordion class="lesson-accordion" :open="true">
      <h2 slot="header" class="accordion-header">
        <ContentTypeIcon :content-type="ContentType.LESSON" />
        {{ $t('labels.lesson') }}
      </h2>

      <div slot="icon">
        <StatusCircle
          :fill="
            lessonCompleted ? StatusCircleFill.FULL : StatusCircleFill.NONE
          "
          :title="
            $t(
              lessonCompleted
                ? 'components.chapter-section-status-circle.lesson-completed'
                : 'components.chapter-section-status-circle.lesson-not-completed'
            )
          "
        />
      </div>

      <template slot="default">
        <Lesson
          class="chapter-section-part"
          :lesson-id="lessonId"
          :hide-title="true"
        />
        <LessonUnderstoodButtons
          :lesson-id="lessonId"
          @change="updateChapterStatus"
        />
      </template>
    </Accordion>

    <Accordion
      v-if="has5SharpTest"
      class="self-training-accordion"
      :open="lessonCompleted"
    >
      <h2 slot="header" class="accordion-header">
        <ContentTypeIcon :content-type="ContentType.SELF_TRAINING" />
        {{ $t('labels.self-training') }}
      </h2>

      <div slot="icon">
        <StatusCircle
          :fill="
            selfTrainingCompleted
              ? StatusCircleFill.FULL
              : StatusCircleFill.NONE
          "
          :title="
            $t(
              selfTrainingCompleted
                ? 'components.chapter-section-status-circle.self-training-completed'
                : 'components.chapter-section-status-circle.self-training-not-completed'
            )
          "
        />
      </div>

      <template slot="default">
        <FiveSharp
          :lesson-id="lessonId"
          :class-level="fiveSharpClassLevel"
          @finished="updateChapterStatus"
        />
      </template>
    </Accordion></div
></template>

<script>
import Accordion from '@/components/ui/Accordion'
import ContentTypeIcon from '@/components/ui/ContentTypeIcon'
import ErrorMessage from '@/components/ui/ErrorMessage'
import FiveSharp from '@/components/training/FiveSharp'
import Lesson from '@/components/lessons/Lesson'
import LessonUnderstoodButtons from '@/components/lessons/LessonUnderstoodButtons'
import LoadingOverlay from '@/components/LoadingOverlay'
import StatusCircle, { StatusCircleFill } from '@/components/ui/StatusCircle'
import TextToSpeech from '@/components/accessibility/TextToSpeech'
import ThreeDButton from '@/components/ThreeDButton'

import { ContentType } from '@/enums'

import { AuthStoreNamespacedTypes } from '@/store/auth'
import { BookStoreNamespacedTypes } from '@/store/books'
import { LessonStoreNamespacedTypes } from '@/store/lessons'

import { clamp } from '@/utils/math'
import { isUserSelectedUnderstoodValue } from '@/utils/lessons'

export default {
  name: 'ChapterSectionLesson',
  components: {
    ErrorMessage,
    FiveSharp,
    Accordion,
    ContentTypeIcon,
    Lesson,
    LessonUnderstoodButtons,
    LoadingOverlay,
    StatusCircle,
    TextToSpeech,
    ThreeDButton,
  },
  props: {
    bookId: { type: Number, required: true },
    chapterId: { type: Number, required: true },
    lessonId: { type: Number, required: true },
  },
  data() {
    return { ContentType, StatusCircleFill }
  },
  computed: {
    /**
     * The data for the book
     * @return {Object}
     */
    bookData() {
      return this.$store.getters[BookStoreNamespacedTypes.getters.GET_BOOK](
        this.bookId
      )
    },

    /**
     * @return {Object}
     */
    lesson() {
      return this.$store.getters[LessonStoreNamespacedTypes.getters.GET_LESSON](
        this.lessonId
      )
    },

    /**
     * @return {String}
     */
    lessonError() {
      return this.$store.getters[
        LessonStoreNamespacedTypes.getters.GET_LESSON_ERROR
      ](this.lessonId)
    },

    /**
     * @return {LessonUnderstood|null}
     */
    lessonUnderstood() {
      return this.$store.getters[
        LessonStoreNamespacedTypes.getters.GET_UNDERSTOOD_STATUS
      ](this.lessonId)
    },

    /**
     * @return {boolean}
     */
    has5SharpTest() {
      return this.$store.getters[
        LessonStoreNamespacedTypes.getters.HAS_5_SHARP_TEST
      ](this.lessonId)
    },

    /**
     * @return {boolean}
     */
    lessonCompleted() {
      return isUserSelectedUnderstoodValue(this.lessonUnderstood)
    },

    /**
     * The class level to use in Five Sharp.
     * Returns the class level of the current user clamped between the min and max class of the book.
     * @return {Number}
     */
    fiveSharpClassLevel() {
      return clamp(
        this.$store.getters[AuthStoreNamespacedTypes.getters.GET_USER_CLASS](),
        this.bookData.minClass,
        this.bookData.maxClass
      )
    },

    /**
     * @return {boolean}
     */
    selfTrainingCompleted() {
      return this.$store.getters[
        BookStoreNamespacedTypes.getters.IS_SELF_TRAINING_COMPLETED
      ](this.chapterId, this.lessonId)
    },
  },
  methods: {
    fetchLessonData() {
      this.$store.dispatch(
        LessonStoreNamespacedTypes.actions.FETCH_LESSON,
        this.lessonId
      )
    },
    updateChapterStatus() {
      this.$store.dispatch(
        BookStoreNamespacedTypes.actions.FETCH_CHAPTER_STATUS,
        this.chapterId
      )
    },
  },
  watch: {
    lessonId: {
      handler: function() {
        this.fetchLessonData()
      },
      immediate: true,
    },
  },
}
</script>

<style lang="scss" scoped>
.chapter-section-part {
  background: $white;
  border-radius: 1em;
}

.chapter-section-heading {
  @media (max-width: $minWidthTablet) {
    text-align: center;
  }
}

.accordion {
  margin-bottom: 4em;

  @media (max-width: $minWidthSmallTablet) {
    border-radius: 0;
  }
}

.accordion-header {
  color: $textColor;
  text-transform: uppercase;
  font-size: 1.35em;

  .content-type-icon {
    $size: 1.5em;

    display: inline-block;
    height: $size;
    width: $size;
    vertical-align: middle;
    margin: 0 0.5em 0.1em 0;
  }
}

.lesson-accordion ::v-deep .body-inner {
  padding: 0 0 4em;
}

::v-deep {
  .lesson {
    .lesson-section {
      padding: 2em 3em 3em;

      &:not(:last-child) {
        border-bottom: 2px solid $grayLighter;
      }
    }

    .lesson-section-title {
      font-size: 1.5em;
    }
  }
}
</style>
